import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, ChevronDown } from "lucide-react";

const PLAYBOOK_ITEMS = [  // eslint-disable-line no-unused-vars
  // Phase L: Playbook pages still exist for SEO backlinks but are not linked in nav.
  { to: "/playbooks/uf", label: "UF Transfers" },
  { to: "/playbooks/ucf-directconnect", label: "UCF DirectConnect" },
  { to: "/playbooks/harvard", label: "Harvard Transfer" },
];

const COLLEGES_ITEMS = [
  { to: "/community-colleges", label: "Community Colleges" },
  { to: "/universities",       label: "Universities" },
  { to: "/transfer-map",       label: "Transfer Map" },
];

const RESOURCE_ITEMS = [
  { to: "/playbook", label: "The New Ivy Playbook" },
  { to: "/resources", label: "Guides" },
  { to: "/blog", label: "Blog" },
];

// Phase N — Company dropdown (About, Methodology, Team, Impact, Press, Company)
const COMPANY_ITEMS = [
  { to: "/about", label: "About" },
  { to: "/methodology", label: "Methodology" },
  { to: "/team", label: "Team" },
  { to: "/impact", label: "Impact" },
  { to: "/press", label: "Press" },
  { to: "/company", label: "Company one-pager" },
];

const COMPANY_PATHS = new Set(COMPANY_ITEMS.map((i) => i.to));

const PRIMARY_LINKS = [
  { to: "/", label: "Home" },
  { to: "/florida-transfer-system", label: "Florida Transfer System" },
  { to: "/majors", label: "Majors" },
  { to: "/roadmaps", label: "Roadmaps" },
  { to: "/transfer-audit", label: "Transfer Audit" },
  { to: "/pricing", label: "Pricing" },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function TopNav() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const onDark = scrolled;
  const navBg = onDark ? "bg-ivy/95 border-ivy-light" : "bg-bone/85 border-stone/30";
  const linkBase = onDark ? "text-bone-dim hover:text-bone" : "text-ink-soft hover:text-ivy";
  const linkActive = onDark ? "text-bone" : "text-ivy";
  const brandColor = onDark ? "text-bone" : "text-ink";
  const taglineColor = onDark ? "text-brass-soft" : "text-brass";

  const isPlaybooksActive = location.pathname.startsWith("/playbooks");  // eslint-disable-line no-unused-vars
  const isCollegesActive = location.pathname.startsWith("/community-colleges") || location.pathname.startsWith("/universities");
  const isResourcesActive = location.pathname === "/resources" || location.pathname.startsWith("/resources/") || location.pathname.startsWith("/blog");
  const isCompanyActive = COMPANY_PATHS.has(location.pathname);

  return (
    <header
      className={`sticky top-0 z-40 w-full backdrop-blur border-b transition-colors duration-300 ${navBg}`}
      data-testid="top-nav"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-16">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className={`text-[15px] uppercase tracking-[0.18em] font-semibold ${brandColor}`}
            data-testid="brand-link"
          >
            Beyond Valencia
          </Link>
          <span className={`hidden xl:block h-4 w-px ${onDark ? "bg-bone-dim/30" : "bg-stone/50"}`} />
          <span className={`hidden xl:block text-[10px] uppercase tracking-[0.22em] ${taglineColor}`}>
            Florida Transfer Admissions Strategy
          </span>
        </div>

        <nav className="hidden lg:flex items-center gap-6">
          {/* Home + Florida Transfer System */}
          {PRIMARY_LINKS.slice(0, 2).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-[13px] font-medium transition-colors ${isActive ? linkActive : linkBase}`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}

          {/* Phase N — Company dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center gap-1 text-[13px] font-medium transition-colors ${isCompanyActive ? linkActive : linkBase}`}
                data-testid="nav-company"
              >
                Company <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 rounded-none">
              {COMPANY_ITEMS.map((p) => (
                <DropdownMenuItem
                  key={p.to}
                  onClick={() => navigate(p.to)}
                  data-testid={`nav-company-${p.to.slice(1).replace(/\//g, "-")}`}
                  className="rounded-none"
                >
                  {p.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Colleges dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center gap-1 text-[13px] font-medium transition-colors ${isCollegesActive ? linkActive : linkBase}`}
                data-testid="nav-colleges"
              >
                Colleges <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 rounded-none">
              {COLLEGES_ITEMS.map((p) => (
                <DropdownMenuItem
                  key={p.to}
                  onClick={() => navigate(p.to)}
                  data-testid={`nav-colleges-${p.to.split("/").pop()}`}
                  className="rounded-none"
                >
                  {p.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Majors, Roadmaps, Transfer Audit, Pricing */}
          {PRIMARY_LINKS.slice(2).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-[13px] font-medium transition-colors ${isActive ? linkActive : linkBase}`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}

          {/* Resources dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center gap-1 text-[13px] font-medium transition-colors ${isResourcesActive ? linkActive : linkBase}`}
                data-testid="nav-resources"
              >
                Resources <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44 rounded-none">
              {RESOURCE_ITEMS.map((p) => (
                <DropdownMenuItem
                  key={p.to}
                  onClick={() => navigate(p.to)}
                  className="rounded-none"
                  data-testid={`nav-resource-${p.label.toLowerCase()}`}
                >
                  {p.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/transfer-audit"
            className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5 transition-colors"
            data-testid="nav-schedule-cta"
          >
            Schedule
          </Link>
          {loading ? (
            <div className="text-xs text-stone">…</div>
          ) : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className={`flex items-center gap-2 text-sm ${onDark ? "text-bone" : "text-ink"} hover:text-ivy transition-colors`}
                >
                  <span className="w-8 h-8 rounded-none bg-ivy text-bone flex items-center justify-center text-xs font-medium">
                    {(user.name || user.email || "?")[0].toUpperCase()}
                  </span>
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-none">
                <DropdownMenuLabel className="font-normal text-xs text-stone">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="rounded-none" onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">Dashboard</DropdownMenuItem>
                <DropdownMenuItem className="rounded-none" onClick={handleLogout} data-testid="menu-logout">Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="signin-link">
              <button className={`text-[11px] uppercase tracking-[0.18em] font-medium ${onDark ? "text-bone hover:text-brass-soft" : "text-ink hover:text-ivy"}`}>
                Sign In
              </button>
            </Link>
          )}
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-3">
          <Link
            to="/transfer-audit"
            className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[10px] font-semibold py-2 px-3 transition-colors"
          >
            Schedule
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className={`p-2 ${onDark ? "text-bone" : "text-ink"} hover:text-ivy`}
                data-testid="mobile-nav-toggle"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-bone w-80 rounded-none overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="text-base uppercase tracking-[0.18em] font-semibold text-ink text-left">
                  Beyond Valencia
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-8 flex flex-col gap-4" data-testid="mobile-nav-list">
                {PRIMARY_LINKS.slice(0, 2).map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `text-sm font-medium transition-colors ${isActive ? "text-ivy" : "text-ink-soft"} hover:text-ivy`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div data-testid="mobile-nav-company-group">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-brass mb-2">Company</div>
                  <div className="flex flex-col gap-2 pl-3 border-l border-stone/40">
                    {COMPANY_ITEMS.map((p) => (
                      <NavLink
                        key={p.to}
                        to={p.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-company-${p.to.slice(1).replace(/\//g, "-")}`}
                        className={({ isActive }) =>
                          `text-sm transition-colors ${isActive ? "text-ivy" : "text-ink-soft"} hover:text-ivy`
                        }
                      >
                        {p.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
                <div data-testid="mobile-nav-colleges-group">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-brass mb-2">Colleges</div>
                  <div className="flex flex-col gap-2 pl-3 border-l border-stone/40">
                    <NavLink
                      to="/community-colleges"
                      onClick={() => setOpen(false)}
                      end
                      data-testid="mobile-nav-colleges-all"
                      className={({ isActive }) =>
                        `text-sm font-medium transition-colors ${isActive ? "text-ivy" : "text-ink-soft"} hover:text-ivy`
                      }
                    >
                      All Colleges
                    </NavLink>
                    {COLLEGES_ITEMS.map((p) => (
                      <NavLink
                        key={p.to}
                        to={p.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-colleges-${p.to.split("/").pop()}`}
                        className={({ isActive }) =>
                          `text-sm transition-colors ${isActive ? "text-ivy" : "text-ink-soft"} hover:text-ivy`
                        }
                      >
                        {p.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
                {PRIMARY_LINKS.slice(2).map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `text-sm font-medium transition-colors ${isActive ? "text-ivy" : "text-ink-soft"} hover:text-ivy`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-brass mb-2">Resources</div>
                  <div className="flex flex-col gap-2 pl-3 border-l border-stone/40">
                    {RESOURCE_ITEMS.map((p) => (
                      <NavLink
                        key={p.to}
                        to={p.to}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `text-sm transition-colors ${isActive ? "text-ivy" : "text-ink-soft"} hover:text-ivy`
                        }
                      >
                        {p.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
                <NavLink to="/contact" onClick={() => setOpen(false)} className="text-sm font-medium text-ink-soft hover:text-ivy">
                  Contact
                </NavLink>
                <div className="mt-4 pt-4 border-t border-stone/40">
                  {user ? (
                    <>
                      <Link to="/dashboard" onClick={() => setOpen(false)}>
                        <button className="w-full mb-2 border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors">Dashboard</button>
                      </Link>
                      <button onClick={() => { setOpen(false); handleLogout(); }} className="w-full text-ink-soft hover:text-ivy uppercase tracking-[0.18em] text-[11px] font-semibold py-3 transition-colors">
                        Logout
                      </button>
                    </>
                  ) : (
                    <Link to="/login" onClick={() => setOpen(false)}>
                      <button className="w-full bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 transition-colors">
                        Sign In
                      </button>
                    </Link>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
